import { create } from "zustand";

import type { SongShareData } from "../steps/DetailsAndSharesStep";
import type {
  SpotifyAlbumInfoResponse,
  SpotifyPlaylistInfoResponse,
  SpotifyTrackItem,
} from "../types/spotify";

/** Whether the wizard was entered via "Add Album" or "Add Playlist" */
export type SourceType = "album" | "playlist";

/** Union of possible source metadata objects */
export type SourceData = SpotifyAlbumInfoResponse | SpotifyPlaylistInfoResponse;

// Selected track state for the workflow
export type SelectedTrack = SpotifyTrackItem & {
  isSelected: boolean;
  isRemix?: boolean;
  containsSamples?: boolean;
  isDuplicate?: boolean; // Track if this ISRC already exists in user's account
  isSuspectedDuplicate?: boolean; // Track if this might be similar to an existing work (account or intra-source)
  suspectedWorkCode?: string; // The workcode of the work this recording will be attached to (account match)
  suspectedWorkTitle?: string; // The title of the work this recording will be attached to (account match)
  suspectedAlbumTrackId?: string; // The ID of another track in THIS source that this matches
  suspectedAlbumTrackTitle?: string; // The title of the track in THIS source that this matches
  isOriginalInAlbum?: boolean; // Is this the "original" track (true) or a variant (false) in intra-source matches
  reviewAction?: "dismiss" | "attach-recording" | null; // User's decision for suspected duplicates
  auxiliaryWriters?: string[]; // Writer codes of additional writers attached when marking as new song
};

// Define the store state
type MultiSongStoreState = {
  sourceType: SourceType;
  sourceData: SourceData | null;
  tracks: SelectedTrack[];
  apiToken: string | null;
  isLoading: boolean;
  error: string | null;
  songShares: Record<string, SongShareData>;
  currentSongIndex: number;
  userISRCs: Set<string>; // Cache of user's existing ISRCs for deduplication
};

// Define the store actions
type MultiSongStoreActions = {
  setSourceType: (sourceType: SourceType) => void;
  setSourceData: (sourceData: SourceData | null) => void;
  setTracks: (tracks: SpotifyTrackItem[]) => void;
  setApiToken: (token: string | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  toggleTrackSelection: (trackId: string) => void;
  selectAllTracks: (selected: boolean) => void;
  toggleTrackRemix: (trackId: string) => void;
  toggleTrackContainsSamples: (trackId: string) => void;
  setAllTracksRemix: (isRemix: boolean) => void;
  setAllTracksContainsSamples: (containsSamples: boolean) => void;
  getSelectedTracks: () => SelectedTrack[];
  setSongShares: (songShares: Record<string, SongShareData>) => void;
  setCurrentSongIndex: (index: number) => void;
  setUserISRCs: (isrcs: string[]) => void; // Set user's existing ISRCs for deduplication
  markTrackAsSuspectedDuplicate: (trackId: string, workCode: string, workTitle: string) => void;
  markTrackAsSuspectedIntraAlbumDuplicate: (trackId: string, targetTrackId: string, targetTrackTitle: string, isOriginal: boolean) => void;
  clearSuspectedDuplicate: (trackId: string) => void; // Clear suspected duplicate flags (for false positives)
  reclassifyVariantAsAccountMatch: (trackId: string, workCode: string, workTitle: string) => void; // Reclassify an intra-album variant as an account-level match (cascade when parent is confirmed as recording)
  setTrackReviewAction: (trackId: string, action: "dismiss" | "attach-recording" | null) => void;
  updateTrackTargetWork: (trackId: string, workCode: string, workTitle: string) => void; // Update which work a recording will be attached to
  updateTrackTargetAlbumTrack: (trackId: string, targetTrackId: string, targetTrackTitle: string) => void; // Update which album track a recording will be attached to
  attachAuxiliaryWriters: (trackId: string, writerCodes: string[]) => void; // Attach additional writers to a track
  getSuspectedDuplicateTracks: () => SelectedTrack[];
  getUnreviewedTracks: () => SelectedTrack[];
  clearState: () => void;
};

// Create the store
const useMultiSongStore = create<MultiSongStoreState & MultiSongStoreActions>((set, get) => ({
  sourceType: "album",
  sourceData: null,
  tracks: [],
  apiToken: null,
  isLoading: false,
  error: null,
  songShares: {},
  currentSongIndex: 0,
  userISRCs: new Set(),

  setSourceType: sourceType => set({ sourceType }),

  setSourceData: sourceData => set({ sourceData }),

  setTracks: (tracks) => {
    const state = get();
    // Preserve existing track state when tracks are re-set (e.g., when navigating back in wizard)
    const existingTracksMap = new Map(state.tracks.map(t => [t.id, t]));

    set({
      tracks: tracks.map((track) => {
        const isrc = track.external_ids?.isrc;
        const isDuplicate = isrc !== null && isrc !== undefined && isrc !== "" ? state.userISRCs.has(isrc) : false;
        const existingTrack = existingTracksMap.get(track.id);

        // If track exists, preserve user decisions and state
        if (existingTrack) {
          return {
            ...track,
            // Preserve user-modified state
            isSelected: existingTrack.isSelected,
            isRemix: existingTrack.isRemix,
            containsSamples: existingTrack.containsSamples,
            isDuplicate,
            // Preserve suspected duplicate detection state
            isSuspectedDuplicate: existingTrack.isSuspectedDuplicate,
            suspectedWorkCode: existingTrack.suspectedWorkCode,
            suspectedWorkTitle: existingTrack.suspectedWorkTitle,
            suspectedAlbumTrackId: existingTrack.suspectedAlbumTrackId,
            suspectedAlbumTrackTitle: existingTrack.suspectedAlbumTrackTitle,
            isOriginalInAlbum: existingTrack.isOriginalInAlbum,
            // Preserve user review decisions (CRITICAL for dismissed tracks)
            reviewAction: existingTrack.reviewAction,
            auxiliaryWriters: existingTrack.auxiliaryWriters,
          };
        }

        // New track - initialize with defaults
        return {
          ...track,
          isSelected: !isDuplicate, // Don't auto-select duplicates
          isRemix: false,
          containsSamples: false,
          isDuplicate,
        };
      }),
    });
  },

  setApiToken: apiToken => set({ apiToken }),

  setIsLoading: isLoading => set({ isLoading }),

  setError: error => set({ error }),

  toggleTrackSelection: trackId => set((state) => {
    const trackIndex = state.tracks.findIndex(track => track.id === trackId);
    if (trackIndex === -1)
      return state;

    const newTracks = [...state.tracks];
    newTracks[trackIndex] = {
      ...newTracks[trackIndex],
      isSelected: !newTracks[trackIndex].isSelected,
    };
    return { tracks: newTracks };
  }),

  selectAllTracks: selected => set(state => ({
    tracks: state.tracks.map(track => ({
      ...track,
      isSelected: track.isDuplicate ? track.isSelected : selected, // Don't change selection of duplicates
    })),
  })),

  toggleTrackRemix: trackId => set((state) => {
    const trackIndex = state.tracks.findIndex(track => track.id === trackId);
    if (trackIndex === -1)
      return state;

    const newTracks = [...state.tracks];
    newTracks[trackIndex] = {
      ...newTracks[trackIndex],
      isRemix: !newTracks[trackIndex].isRemix,
    };
    return { tracks: newTracks };
  }),

  toggleTrackContainsSamples: trackId => set((state) => {
    const trackIndex = state.tracks.findIndex(track => track.id === trackId);
    if (trackIndex === -1)
      return state;

    const newTracks = [...state.tracks];
    newTracks[trackIndex] = {
      ...newTracks[trackIndex],
      containsSamples: !newTracks[trackIndex].containsSamples,
    };
    return { tracks: newTracks };
  }),

  setAllTracksRemix: isRemix => set(state => ({
    tracks: state.tracks.map(track => ({ ...track, isRemix })),
  })),

  setAllTracksContainsSamples: containsSamples => set(state => ({
    tracks: state.tracks.map(track => ({ ...track, containsSamples })),
  })),

  getSelectedTracks: () => {
    return get().tracks.filter(track => track.isSelected);
  },

  setSongShares: songShares => set({ songShares }),

  setCurrentSongIndex: currentSongIndex => set({ currentSongIndex }),

  setUserISRCs: (isrcs: string[]) => set((state) => {
    const newUserISRCs = new Set(isrcs);

    // First pass: determine which tracks are duplicates
    const duplicateTrackIds = new Set<string>();
    state.tracks.forEach((track) => {
      const isrc = track.external_ids?.isrc;
      const isDuplicate = isrc !== null && isrc !== undefined && isrc !== "" ? newUserISRCs.has(isrc) : false;
      if (isDuplicate) {
        duplicateTrackIds.add(track.id);
      }
    });

    // Second pass: update tracks
    const updatedTracks = state.tracks.map((track) => {
      const isrc = track.external_ids?.isrc;
      const isDuplicate = isrc !== null && isrc !== undefined && isrc !== "" ? newUserISRCs.has(isrc) : false;

      // Check if this track's suspected album target is now an ISRC duplicate
      // If so, clear the variant status - can't be a variant of a crossed-out track
      const targetIsDuplicate = track.suspectedAlbumTrackId != null && duplicateTrackIds.has(track.suspectedAlbumTrackId);

      // When clearing intra-album variant status, also clear isSuspectedDuplicate
      // This allows the account-level match detection to properly re-run
      const shouldClearVariantStatus = targetIsDuplicate && !isDuplicate;

      return {
        ...track,
        isDuplicate,
        isSelected: track.isSelected && !isDuplicate, // Unselect if it becomes a duplicate
        // Clear intra-album variant status if:
        // 1. This track becomes an ISRC duplicate, OR
        // 2. This track's target (suspectedAlbumTrackId) becomes an ISRC duplicate
        ...((isDuplicate || targetIsDuplicate)
          ? {
              suspectedAlbumTrackId: undefined,
              suspectedAlbumTrackTitle: undefined,
              isOriginalInAlbum: undefined,
            }
          : {}),
        // Also clear isSuspectedDuplicate so account-level match can re-detect it
        ...(shouldClearVariantStatus
          ? {
              isSuspectedDuplicate: false,
              reviewAction: null,
            }
          : {}),
      };
    });
    return { userISRCs: newUserISRCs, tracks: updatedTracks };
  }),

  markTrackAsSuspectedDuplicate: (trackId: string, workCode: string, workTitle: string) => set((state) => {
    const trackIndex = state.tracks.findIndex(track => track.id === trackId);
    if (trackIndex === -1)
      return state;

    const existingTrack = state.tracks[trackIndex];
    const newTracks = [...state.tracks];
    newTracks[trackIndex] = {
      ...newTracks[trackIndex],
      isSuspectedDuplicate: true,
      suspectedWorkCode: workCode,
      suspectedWorkTitle: workTitle,
      // Preserve existing reviewAction if user already reviewed this track
      reviewAction: existingTrack.reviewAction || null,
    };
    return { tracks: newTracks };
  }),

  markTrackAsSuspectedIntraAlbumDuplicate: (trackId: string, targetTrackId: string, targetTrackTitle: string, isOriginal: boolean) => set((state) => {
    const trackIndex = state.tracks.findIndex(track => track.id === trackId);
    if (trackIndex === -1)
      return state;

    const existingTrack = state.tracks[trackIndex];
    const newTracks = [...state.tracks];
    newTracks[trackIndex] = {
      ...newTracks[trackIndex],
      isSuspectedDuplicate: true,
      suspectedAlbumTrackId: targetTrackId,
      suspectedAlbumTrackTitle: targetTrackTitle,
      isOriginalInAlbum: isOriginal,
      // Preserve existing reviewAction if user already reviewed this track
      reviewAction: existingTrack.reviewAction || null,
    };
    return { tracks: newTracks };
  }),

  setTrackReviewAction: (trackId: string, action: "dismiss" | "attach-recording" | null) => set((state) => {
    const trackIndex = state.tracks.findIndex(track => track.id === trackId);
    if (trackIndex === -1)
      return state;

    const newTracks = [...state.tracks];
    newTracks[trackIndex] = {
      ...newTracks[trackIndex],
      reviewAction: action,
    };
    return { tracks: newTracks };
  }),

  updateTrackTargetWork: (trackId: string, workCode: string, workTitle: string) => set((state) => {
    const trackIndex = state.tracks.findIndex(track => track.id === trackId);
    if (trackIndex === -1)
      return state;

    const newTracks = [...state.tracks];
    newTracks[trackIndex] = {
      ...newTracks[trackIndex],
      suspectedWorkCode: workCode,
      suspectedWorkTitle: workTitle,
    };
    return { tracks: newTracks };
  }),

  updateTrackTargetAlbumTrack: (trackId: string, targetTrackId: string, targetTrackTitle: string) => set((state) => {
    const trackIndex = state.tracks.findIndex(track => track.id === trackId);
    if (trackIndex === -1)
      return state;

    const newTracks = [...state.tracks];
    newTracks[trackIndex] = {
      ...newTracks[trackIndex],
      suspectedAlbumTrackId: targetTrackId,
      suspectedAlbumTrackTitle: targetTrackTitle,
    };
    return { tracks: newTracks };
  }),

  // Clear all suspected duplicate flags - resets track to a normal state
  clearSuspectedDuplicate: (trackId: string) => set((state) => {
    const trackIndex = state.tracks.findIndex(track => track.id === trackId);
    if (trackIndex === -1)
      return state;

    const newTracks = [...state.tracks];
    newTracks[trackIndex] = {
      ...newTracks[trackIndex],
      isSuspectedDuplicate: false,
      suspectedWorkCode: undefined,
      suspectedWorkTitle: undefined,
      suspectedAlbumTrackId: undefined,
      suspectedAlbumTrackTitle: undefined,
      isOriginalInAlbum: undefined,
      reviewAction: "dismiss", // Mark as dismissed to prevent re-detection
      auxiliaryWriters: undefined,
    };
    return { tracks: newTracks };
  }),

  // Reclassify an intra-album variant as an account-level match.
  // Used when a parent track is confirmed as a recording of an account work —
  // any variants pointing at that parent should now match the same account work instead.
  // Clears variant fields, sets account match fields, and resets reviewAction to null
  // so the track appears as "needs review" with the correct account-level match context.
  reclassifyVariantAsAccountMatch: (trackId: string, workCode: string, workTitle: string) => set((state) => {
    const trackIndex = state.tracks.findIndex(track => track.id === trackId);
    if (trackIndex === -1)
      return state;

    const newTracks = [...state.tracks];
    newTracks[trackIndex] = {
      ...newTracks[trackIndex],
      // Set account-level match fields
      isSuspectedDuplicate: true,
      suspectedWorkCode: workCode,
      suspectedWorkTitle: workTitle,
      // Clear intra-album variant fields
      suspectedAlbumTrackId: undefined,
      suspectedAlbumTrackTitle: undefined,
      isOriginalInAlbum: undefined,
      // Reset review action so the track shows as "needs review"
      reviewAction: null,
    };
    return { tracks: newTracks };
  }),

  attachAuxiliaryWriters: (trackId: string, writerCodes: string[]) => set((state) => {
    const trackIndex = state.tracks.findIndex(track => track.id === trackId);
    if (trackIndex === -1)
      return state;

    const newTracks = [...state.tracks];
    newTracks[trackIndex] = {
      ...newTracks[trackIndex],
      auxiliaryWriters: writerCodes,
    };
    return { tracks: newTracks };
  }),

  getSuspectedDuplicateTracks: () => {
    return get().tracks.filter(track =>
      track.isSuspectedDuplicate
      && !track.isDuplicate
      && track.isSelected
      && track.reviewAction !== "dismiss",
    );
  },

  getUnreviewedTracks: () => {
    return get().tracks.filter(track =>
      track.isSuspectedDuplicate
      && !track.isDuplicate
      && track.isSelected
      && track.reviewAction === null,
    );
  },

  clearState: () => set({
    sourceType: "album",
    sourceData: null,
    tracks: [],
    apiToken: null,
    isLoading: false,
    error: null,
    songShares: {},
    currentSongIndex: 0,
    userISRCs: new Set(),
  }),
}));

export default useMultiSongStore;

/** @deprecated Use useMultiSongStore instead. Alias kept for backwards compatibility during migration. */
export const useAlbumStore = useMultiSongStore;
